"use client"

import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import IconChevronLeft from "@/app/(site)/_components/Atoms/Icons/IconChevronLeft"
import IconChevronRight from "@/app/(site)/_components/Atoms/Icons/IconChevronRight"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { FOCUS_VISIBLE_INSET_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { useLangCode } from "@/src/utils/locale/useLangCode"
import { t } from "@/translations"
import { memo, useRef, useState } from "react"

type Props = {
    images: string[]
    isAboveTheFold?: boolean
    aspectRatio?: string
    objectFit?: "contain" | "cover"
    firstImageOverride?: string
    imageSizes?: string
    altText?: string
    imageHref?: string
    onImageClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

type DotState = { active: boolean; small: boolean }

/** Returns a max 4-dot sliding window representing the current position in the image list. */
function getDotStates(idx: number, count: number): DotState[] {
    if (count <= 4) {
        return Array.from({ length: count }, (_, i) => ({
            active: i === idx,
            small: false
        }))
    }

    const pinnedLeft = idx <= 1
    const pinnedRight = idx >= count - 2

    if (pinnedLeft) {
        return [
            { active: idx === 0, small: false },
            { active: idx === 1, small: false },
            { active: false, small: false },
            { active: false, small: true }
        ]
    }

    if (pinnedRight) {
        const positionFromRight = count - 1 - idx // 0 = last, 1 = second-to-last
        return [
            { active: false, small: true },
            { active: positionFromRight === 1, small: false },
            { active: positionFromRight === 0, small: false },
            { active: false, small: false }
        ]
    }

    // Middle: active dot stays at position 2, edges are small
    return [
        { active: false, small: true },
        { active: false, small: false },
        { active: true, small: false },
        { active: false, small: true }
    ]
}

export default memo(function ProductImageSlider({
    images,
    isAboveTheFold = false,
    aspectRatio = "0.7",
    objectFit = "contain",
    firstImageOverride,
    imageSizes = "(max-width: 768px) 50vw, 33vw",
    altText = "",
    imageHref,
    onImageClick
}: Props) {
    const langCode = useLangCode()
    const containerRef = useRef<HTMLDivElement>(null)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [hasInteracted, setHasInteracted] = useState(false)
    const touchStartX = useRef<number | null>(null)

    const total = images.length

    const navigate = (dir: 1 | -1) => (e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()
        if (!hasInteracted) setHasInteracted(true)
        setCurrentIndex(i => (i + dir + total) % total)
    }

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX
    }

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return
        const delta = e.changedTouches[0].clientX - touchStartX.current
        if (Math.abs(delta) > 40) {
            if (!hasInteracted) setHasInteracted(true)
            setCurrentIndex(i => (i + (delta < 0 ? 1 : -1) + total) % total)
        }
        touchStartX.current = null
    }

    const dotStates = getDotStates(currentIndex, total)

    const slides = images.map(
        (src, i) =>
            (i === currentIndex ||
                (hasInteracted && Math.abs(i - currentIndex) <= 1)) && (
                <BaseImage
                    key={src}
                    src={i === 0 && firstImageOverride ? firstImageOverride : src}
                    alt={imageHref ? "" : altText}
                    fill
                    sizes={imageSizes}
                    preload={isAboveTheFold && i === 0}
                    loading={isAboveTheFold && i === 0 ? undefined : "lazy"}
                    className={clsx(
                        "absolute inset-0 h-full w-full transition-opacity duration-200 ease-in-out",
                        objectFit === "contain" ? "object-contain" : "object-cover",
                        i === currentIndex ? "opacity-100" : "opacity-0"
                    )}
                    draggable={false}
                />
            )
    )

    const media = (
        <div className="absolute inset-0 overflow-hidden bg-col-bg-calm">{slides}</div>
    )

    return (
        <div
            ref={containerRef}
            className="group relative w-full"
            style={{ aspectRatio }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {media}

            {/* Transparent overlay rather than a wrapper: an inset ring drawn on
                an ancestor is painted underneath a positioned child, so the
                opaque media layer would hide it. */}
            {imageHref && (
                <DynamicLink
                    href={imageHref}
                    prefetch={false}
                    onClick={onImageClick}
                    className={clsx("absolute inset-0 z-10", FOCUS_VISIBLE_INSET_CLASS)}
                >
                    <span className="sr-only">{altText}</span>
                </DynamicLink>
            )}

            {total > 1 && (
                <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={navigate(-1)}
                        aria-label={t(langCode, "product_page.previous_image")}
                        className={clsx(
                            "card-hover-ui pointer-events-auto relative px-8 py-12 opacity-0 transition-opacity duration-200",
                            "before:absolute before:-inset-x-4 before:-inset-y-4 before:content-['']",
                            "max-lg:hidden",
                            "lg:focus-visible:opacity-100 lg:group-focus-within:opacity-100 lg:group-hover:opacity-100",
                            FOCUS_VISIBLE_INSET_CLASS
                        )}
                    >
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-col-bg-calm">
                            <IconChevronLeft />
                        </div>
                    </button>

                    <button
                        type="button"
                        onClick={navigate(1)}
                        aria-label={t(langCode, "product_page.next_image")}
                        className={clsx(
                            "card-hover-ui pointer-events-auto relative px-8 py-12 opacity-0 transition-opacity duration-200",
                            "before:absolute before:-inset-x-4 before:-inset-y-4 before:content-['']",
                            "max-lg:hidden",
                            "lg:focus-visible:opacity-100 lg:group-focus-within:opacity-100 lg:group-hover:opacity-100",
                            FOCUS_VISIBLE_INSET_CLASS
                        )}
                    >
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-col-bg-calm">
                            <IconChevronRight />
                        </div>
                    </button>

                    <div
                        className="slider-dots card-hover-ui absolute bottom-0 left-0 flex items-center gap-[6px] p-8 transition-opacity duration-200 lg:p-12 lg:opacity-0 lg:group-focus-within:opacity-100 lg:group-hover:opacity-100"
                        aria-hidden="true"
                    >
                        {dotStates.map((dot, i) => (
                            <span
                                key={i}
                                className={clsx(
                                    "block rounded-full bg-col-text-primary transition-all duration-200",
                                    dot.active
                                        ? "h-6 w-6"
                                        : dot.small
                                          ? "h-4 w-4 opacity-50"
                                          : "h-6 w-6 opacity-50"
                                )}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
})
